import { useState } from "react";
import { useAuth } from "@/lib/auth-provider";
import { useNavigate } from "react-router";
import { CommandPalette, useCommandPalette } from "@/components/command-palette";
import { NotificationCenter } from "@/components/notification-center";
import { Menu, Search, Settings, LogOut, Gauge, Moon, Sun } from "lucide-react";
import { useEaseScore } from "@/hooks/use-ease-score";
import { useTheme } from "@/providers/theme-context";

interface TopbarProps {
  onMenuToggle: () => void;
}

export function Topbar({ onMenuToggle }: TopbarProps) {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const commandPalette = useCommandPalette();
  const easeScore = useEaseScore();
  const { resolved: currentTheme, setTheme } = useTheme();

  const handleSignOut = async () => {
    await signOut();
    navigate("/login", { replace: true });
  };

  return (
    <header className="flex h-14 items-center justify-between border-b border-border bg-card px-4">
      {/* Left: hamburger (mobile) + search */}
      <div className="flex items-center gap-3">
        <button
          onClick={onMenuToggle}
          className="rounded-md p-1.5 text-muted-foreground hover:bg-accent lg:hidden"
          aria-label="Toggle sidebar"
        >
          <Menu className="h-5 w-5" />
        </button>

        <button
          onClick={commandPalette.open}
          className="flex items-center gap-2 rounded-md border border-border px-3 py-1.5 text-sm text-muted-foreground hover:bg-accent"
        >
          <Search className="h-4 w-4" />
          <span className="hidden sm:inline text-muted-foreground/60">Search or ask AI...</span>
          <kbd className="ml-2 hidden rounded border border-border px-1.5 py-0.5 text-[10px] text-muted-foreground/50 sm:inline">
            ⌘K
          </kbd>
        </button>
      </div>

      {/* Right: ease score + notifications + user */}
      <div className="flex items-center gap-2">
        {/* C16: Ease Score badge */}
        {easeScore.data && (
          <div className={`flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${
            easeScore.data.score >= 70 ? "bg-[var(--color-success)]/10 text-[var(--color-success)]"
            : easeScore.data.score >= 40 ? "bg-[var(--color-warning)]/10 text-[var(--color-warning)]"
            : "bg-[var(--color-destructive)]/10 text-[var(--color-destructive)]"
          }`}>
            <Gauge className="h-3 w-3" />
            <span className="tabular-nums">{easeScore.data.score}</span>
          </div>
        )}
        <NotificationCenter />

        <div className="relative">
          <button
            onClick={() => setShowUserMenu(!showUserMenu)}
            className="flex items-center gap-2 rounded-md p-1.5 hover:bg-accent"
          >
            <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">
              {(user?.name ?? "U")[0]?.toUpperCase()}
            </div>
            <span className="hidden text-sm font-medium text-foreground sm:inline">
              {user?.name ?? "User"}
            </span>
          </button>

          {showUserMenu && (
            <div className="absolute right-0 top-full z-50 mt-1 w-48 rounded-md border border-border bg-card py-1 shadow-lg">
              <button
                onClick={() => { setTheme(currentTheme === "dark" ? "light" : "dark"); }}
                className="flex w-full items-center gap-2 px-4 py-2 text-sm text-foreground hover:bg-accent"
              >
                {currentTheme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                {currentTheme === "dark" ? "Light mode" : "Dark mode"}
              </button>
              <button
                onClick={() => { navigate("/settings"); setShowUserMenu(false); }}
                className="flex w-full items-center gap-2 px-4 py-2 text-sm text-foreground hover:bg-accent"
              >
                <Settings className="h-4 w-4" /> Settings
              </button>
              <hr className="my-1 border-border" />
              <button
                onClick={handleSignOut}
                className="flex w-full items-center gap-2 px-4 py-2 text-sm text-destructive hover:bg-accent"
              >
                <LogOut className="h-4 w-4" /> Sign out
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Command Palette (Cmd+K) — D.1 */}
      <CommandPalette isOpen={commandPalette.isOpen} onClose={commandPalette.close} />
    </header>
  );
}
